import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import LeadCaptureLogoField from "./LeadCaptureLogoField";

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm " +
  "focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </span>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </label>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <div
      role="switch"
      aria-checked={checked}
      tabIndex={0}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      className="flex items-center justify-between gap-3 cursor-pointer select-none"
    >
      <span className="text-sm text-gray-700">{label}</span>
      <span
        className={`relative inline-flex w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
          checked ? "bg-brand" : "bg-gray-300"
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            checked ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
      </span>
    </div>
  );
}

export default function LeadCaptureSettings({ values, onChange }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const update = (k) => (v) => onChange({ ...values, [k]: v });

  return (
    <div className="space-y-5">
      <Field label="Título del formulario">
        <input
          type="text"
          maxLength={120}
          value={values.title || ""}
          onChange={(e) => update("title")(e.target.value)}
          className={inputClass}
          placeholder="¿Quieres más información?"
        />
      </Field>

      <Field label="Subtítulo (opcional)">
        <input
          type="text"
          maxLength={200}
          value={values.subtitle || ""}
          onChange={(e) => update("subtitle")(e.target.value)}
          className={inputClass}
          placeholder="Te respondemos en 24 horas"
        />
      </Field>

      <LeadCaptureLogoField
        value={values.logo_url}
        onChange={update("logo_url")}
      />

      <Field label="Color principal">
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={values.primary_color || "#10b981"}
            onChange={(e) => update("primary_color")(e.target.value)}
            className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
          />
          <input
            type="text"
            maxLength={7}
            value={values.primary_color || "#10b981"}
            onChange={(e) => update("primary_color")(e.target.value)}
            className={inputClass + " flex-1"}
            placeholder="#10b981"
          />
        </div>
      </Field>

      <div className="space-y-3 pt-2 border-t border-gray-100">
        <Toggle
          checked={values.show_servicio !== false}
          onChange={update("show_servicio")}
          label="Mostrar campo «servicio»"
        />
        <Toggle
          checked={values.show_como_conocio !== false}
          onChange={update("show_como_conocio")}
          label="Mostrar campo «cómo me conociste»"
        />
        <Toggle
          checked={values.require_phone !== false}
          onChange={update("require_phone")}
          label="Teléfono obligatorio"
        />
        <Toggle
          checked={values.require_email !== false}
          onChange={update("require_email")}
          label="Email obligatorio"
        />
      </div>

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="w-full flex items-center justify-between text-sm text-gray-600 hover:text-gray-900 pt-2 border-t border-gray-100"
      >
        <span className="font-medium">Opciones avanzadas</span>
        {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {showAdvanced && (
        <div className="space-y-5 pt-2">
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1.5">
              Tras enviar el formulario
            </span>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "thank_you", label: "Página de gracias" },
                { id: "redirect", label: "Redirigir a otra URL" },
              ].map((opt) => {
                const active = (values.post_submit_mode || "thank_you") === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => update("post_submit_mode")(opt.id)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      active
                        ? "border-brand bg-brand/5 text-brand font-medium"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {values.post_submit_mode !== "redirect" && (
            <Field
              label="Mensaje en la página de gracias"
              hint="Si lo dejas vacío, mostraremos un texto genérico."
            >
              <textarea
                rows={2}
                maxLength={300}
                value={values.thank_you_message || ""}
                onChange={(e) => update("thank_you_message")(e.target.value)}
                className={inputClass + " resize-none"}
                placeholder="¡Gracias! Te llamamos en menos de 24h."
              />
            </Field>
          )}

          {values.post_submit_mode === "redirect" && (
            <Field
              label="URL de redirección"
              hint="URL completa con https://. El visitante será enviado allí tras enviar el formulario."
            >
              <input
                type="url"
                maxLength={500}
                value={values.redirect_url || ""}
                onChange={(e) => update("redirect_url")(e.target.value)}
                className={inputClass}
                placeholder="https://tuweb.com/gracias"
              />
            </Field>
          )}

          <div>
            <Toggle
              checked={!!values.notify_email}
              onChange={update("notify_email")}
              label="Avisarme por email cuando llegue un lead"
            />
            <p className="mt-1 text-xs text-gray-500">
              Te avisaremos al email con el que estás dado de alta en el portal.
            </p>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <span className="block text-sm font-medium text-gray-700 mb-1.5">
              Política de privacidad (RGPD)
            </span>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "template", label: "Usar plantilla ETC" },
                { id: "external", label: "Tengo URL propia" },
              ].map((opt) => {
                const active = (values.privacy_mode || "template") === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => update("privacy_mode")(opt.id)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      active
                        ? "border-brand bg-brand/5 text-brand font-medium"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {values.privacy_mode === "external" ? (
            <Field
              label="Enlace a tu política de privacidad"
              hint="URL completa con https://. Aparecerá bajo el formulario."
            >
              <input
                type="url"
                maxLength={500}
                value={values.privacy_url || ""}
                onChange={(e) => update("privacy_url")(e.target.value)}
                className={inputClass}
                placeholder="https://tuweb.com/privacidad"
              />
            </Field>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-gray-500 -mt-2">
                Generaremos una página automática en{" "}
                <code className="px-1 py-0.5 bg-gray-100 rounded text-[11px]">
                  /l/tu-slug/privacidad
                </code>{" "}
                con estos datos. Todos son obligatorios.
              </p>

              <Field label="Razón social / Nombre fiscal *">
                <input
                  type="text"
                  maxLength={150}
                  value={values.privacy_responsable || ""}
                  onChange={(e) =>
                    update("privacy_responsable")(e.target.value)
                  }
                  className={inputClass}
                  placeholder="Mi Centro Deportivo S.L."
                />
              </Field>

              <Field label="Email corporativo (RGPD) *">
                <input
                  type="email"
                  maxLength={150}
                  value={values.privacy_email || ""}
                  onChange={(e) => update("privacy_email")(e.target.value)}
                  className={inputClass}
                  placeholder="info@mi-centro.com"
                />
              </Field>

              <Field label="Dirección fiscal *">
                <input
                  type="text"
                  maxLength={200}
                  value={values.privacy_address || ""}
                  onChange={(e) => update("privacy_address")(e.target.value)}
                  className={inputClass}
                  placeholder="Calle Ejemplo 123"
                />
              </Field>

              <Field label="Población *">
                <input
                  type="text"
                  maxLength={100}
                  value={values.privacy_city || ""}
                  onChange={(e) => update("privacy_city")(e.target.value)}
                  className={inputClass}
                  placeholder="Madrid"
                />
              </Field>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
